import {
  mdiAccountCog,
  mdiAccountHardHat,
  mdiAlert,
  mdiBookAlphabet,
  mdiChartBar,
  mdiCog,
  mdiOfficeBuilding,
  mdiStore,
  mdiWrench,
} from "@mdi/js";
import { MaterialIcon } from "components/MaterialIcon";
import { Permission } from "components/Permission";
import Claims from "features/auth/claims";
import useFeature from "hooks/useFeature";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  SidebarDivider,
  SidebarMenu,
  SideMenuOptionProps,
} from "widgets/layout/sidebar-header-layout";

const SidebarMainMenu: React.FC = () => {
  const { t } = useTranslation();

  const { isEnabled: isMaintainedDevicesFeatureEnabled } =
    useFeature("maintainedDevices");

  const group1Options = useMemo<SideMenuOptionProps[]>(
    () => [
      {
        label: t("menu.failures"),
        link: "/failures",
        icon: <MaterialIcon path={mdiAlert} />,
        claims: [
          Claims.Failure.Read,
          Claims.Failure.ReadOwn,
          Claims.Failure.ReadOwnLimited,
          Claims.Failure.ReadCallCenter,
        ],
      },
      {
        label: t("menu.contractors"),
        icon: <MaterialIcon path={mdiOfficeBuilding} />,
        link: "/contractors",
        claims: [Claims.Contractor.Read],
      },
      {
        label: t("menu.repairmen"),
        icon: <MaterialIcon path={mdiAccountHardHat} />,
        link: "/repairmen",
        claims: [Claims.Repairman.Read, Claims.Repairman.ReadOwn],
      },
      {
        label: t("menu.inventory"),
        icon: <MaterialIcon path={mdiStore} />,
        link: "/inventory",
        claims: [Claims.Inventory.Read],
      },
      ...(isMaintainedDevicesFeatureEnabled
        ? [
            {
              label: t("menu.maintainedDevices"),
              icon: <MaterialIcon path={mdiWrench} />,
              link: "/maintained-devices",
              claims: [Claims.Device.Read],
            } as SideMenuOptionProps,
          ]
        : []),
    ],
    [isMaintainedDevicesFeatureEnabled, t]
  );

  const group2Options = useMemo<SideMenuOptionProps[]>(
    () => [
      {
        label: t("menu.report"),
        icon: <MaterialIcon path={mdiChartBar} />,
        link: "/report",
        claims: [Claims.Report.Read],
      },
      {
        label: t("menu.dictionaries"),
        icon: <MaterialIcon path={mdiBookAlphabet} />,
        link: "/dictionaries",
        claims: [Claims.MasterData.Read],
      },
      {
        label: t("menu.settings"),
        icon: <MaterialIcon path={mdiCog} />,
        link: "/settings",
        claims: [Claims.Option.Read],
      },
      {
        label: t("menu.rolesAndPermissionsSettings"),
        icon: <MaterialIcon path={mdiAccountCog} />,
        link: "/roles-and-permissions-settings",
        claims: [Claims.Option.Read],
      },
    ],
    [t]
  );

  return (
    <>
      <SidebarMenu options={group1Options} />

      <Permission
        acceptableClaims={[
          Claims.Report.Read,
          Claims.MasterData.Read,
          Claims.Option.Read,
        ]}
      >
        <SidebarDivider />
        <SidebarMenu options={group2Options} />
      </Permission>
    </>
  );
};

export default SidebarMainMenu;
